import { Alert, FormControl } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormControls, FormikCheckbox, FormikNumericField } from 'components';
import { TranslationNamespace } from 'i18n';
import { SciCustomerAlternativesConfig as SciCustomerAlternativesConfigType } from 'types';

type Props = {
  payinTimeoutConfigRequisites?: number;
};

export const SciCustomerAlternativesConfig: React.FC<Props> = ({
  payinTimeoutConfigRequisites = 1, // matches STATUS_TIMEOUT_MINUTES constants in backend
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage.sections.sci_customer_alternatives',
  });

  const { values } = useFormikContext<{
    sciCustomerAlternativesConfig?: SciCustomerAlternativesConfigType;
  }>();

  const showTimeoutWarning = useMemo(() => {
    const { timeout, maxAttempts } = values.sciCustomerAlternativesConfig || {};
    return (
      timeout &&
      maxAttempts &&
      timeout * maxAttempts > payinTimeoutConfigRequisites * 60
    );
  }, [values.sciCustomerAlternativesConfig, payinTimeoutConfigRequisites]);

  const isRequired =
    values.sciCustomerAlternativesConfig?.alternativeAmountEnabled ||
    values.sciCustomerAlternativesConfig?.alternativeTradeMethodEnabled;

  return (
    <FormControls>
      <FormControl>
        <FormikCheckbox
          name="sciCustomerAlternativesConfig.alternativeAmountEnabled"
          label={t('alternative_amount_enabled')}
        />
      </FormControl>
      <FormControl>
        <FormikCheckbox
          name="sciCustomerAlternativesConfig.alternativeTradeMethodEnabled"
          label={t('alternative_trade_method_enabled')}
        />
      </FormControl>
      <FormikNumericField
        name="sciCustomerAlternativesConfig.timeout"
        label={t('timeout')}
        allowNegative={false}
        required={isRequired}
        decimalScale={0}
      />
      <FormikNumericField
        name="sciCustomerAlternativesConfig.maxAttempts"
        label={t('max_attempts')}
        allowNegative={false}
        required={isRequired}
        decimalScale={0}
      />
      {showTimeoutWarning && (
        <Alert severity="warning">
          {t('timeout_warning', { value: payinTimeoutConfigRequisites! * 60 })}
        </Alert>
      )}
    </FormControls>
  );
};
